import React from 'react';
import BlogLayout from '../../components/BlogLayout';
import { Callout } from '../../components/blog/ArticlePrimitives';

const SITE = 'https://www.roofclaw.com';
const SLUG = '/blog/how-to-use-ai-to-automate-calling-google-local-services-leads-openclaw';
const REPO_URL = 'https://github.com/Roofing-Business-Partner/Roofclaw-LocalServiceAd-Agent';

const FAQ_ITEMS = [
  {
    q: 'Can AI call Google Local Services Ads leads automatically?',
    a: 'Yes. A safe implementation polls the Google Ads API for new Local Services Ads leads, writes the lead into the CRM first, then uses a phone provider such as Twilio plus an OpenClaw voice-call workflow to place or receive the call under approved guardrails.',
  },
  {
    q: 'What tools do I need to automate Google LSA lead calls?',
    a: 'The reference stack uses OpenClaw as the agent runtime, Google Ads API for LSA lead retrieval, HubSpot as the CRM, Twilio as the phone provider, and a voice AI stack such as Grok STT, Grok LLM, and Grok TTS.',
  },
  {
    q: 'Should the AI call before creating the CRM record?',
    a: 'No. The safer order is CRM first, call second. Create or update the contact, property, deal, and source context before placing the call so the conversation is never orphaned from the sales process.',
  },
  {
    q: 'Is Twilio required?',
    a: 'Twilio is the recommended default in the RoofClaw launchpad because it is straightforward for dedicated sales numbers. If the business needs routed numbers, marketing attribution, or existing office-number complexity, CallRail or another routing layer may be needed in front.',
  },
  {
    q: 'Can I use the RoofClaw repo without buying RoofClaw?',
    a: 'Yes. The RoofClaw Local Service Ad Agent repo is MIT licensed and can be used as a DIY launchpad. RoofClaw is the done-for-you path when you want this workflow inside a broader business agent rather than a single-purpose automation.',
  },
];

const HOW_TO_STEPS = [
  'Define what the AI phone agent is allowed to say and when it must escalate.',
  'Poll Google Local Services Ads leads through the Google Ads API.',
  'Create or update CRM records before calling the lead.',
  'Use Twilio for the phone carrier layer and configure a public HTTPS webhook.',
  'Run the Twilio onboarding preflight and apply the VoiceUrl only behind a live-mode gate.',
  'Configure OpenClaw Voice Call with a narrow phone-specific prompt.',
  'Choose a voice stack, such as Grok STT to Grok LLM to Grok TTS.',
  'Test on one dedicated Twilio number before routing live LSA calls.',
  'Human-review the first live calls and log every outcome back to the CRM.',
];

const JsonLd = ({ data }) => (
  <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />
);

const StructuredData = () => {
  const faq = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_ITEMS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  const howTo = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to use AI to automate calling Google Local Services Ads leads with OpenClaw',
    description: 'A practical workflow for using OpenClaw, Google Ads API, HubSpot, Twilio, and a voice AI stack to call Google Local Services Ads leads safely.',
    image: `${SITE}/images/blog/ai-google-lsa-calling-openclaw-hero.jpg`,
    totalTime: 'PT2H',
    supply: [
      { '@type': 'HowToSupply', name: 'Google Ads API access for Local Services Ads' },
      { '@type': 'HowToSupply', name: 'Twilio account and phone number' },
      { '@type': 'HowToSupply', name: 'CRM access, such as HubSpot' },
      { '@type': 'HowToSupply', name: 'OpenClaw voice-call capable agent' },
    ],
    tool: [
      { '@type': 'HowToTool', name: 'OpenClaw' },
      { '@type': 'HowToTool', name: 'Twilio' },
      { '@type': 'HowToTool', name: 'Google Ads API' },
      { '@type': 'HowToTool', name: 'HubSpot or compatible CRM' },
    ],
    step: HOW_TO_STEPS.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step,
      text: step,
    })),
  };

  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE}/blog` },
      { '@type': 'ListItem', position: 3, name: 'How to Use AI to Automate Calling Google Local Services Leads with OpenClaw', item: `${SITE}${SLUG}` },
    ],
  };

  return (
    <>
      <JsonLd data={faq} />
      <JsonLd data={howTo} />
      <JsonLd data={breadcrumb} />
    </>
  );
};

const CodeBlock = ({ children }) => (
  <pre className="not-prose my-6 overflow-x-auto rounded-2xl border border-[#F0EFF4]/10 bg-[#05050B] p-5 text-sm text-[#F0EFF4]/75"><code>{children}</code></pre>
);

const RepoCta = () => (
  <div className="not-prose my-12 rounded-[2rem] border border-[#7B61FF]/30 bg-[#18181B] p-8 md:p-10 relative overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-br from-[#7B61FF]/15 via-transparent to-[#10B981]/10" />
    <div className="relative z-10">
      <p className="mb-3 text-xs font-data uppercase tracking-[0.25em] text-[#10B981]">Open-source launchpad</p>
      <h3 className="mb-4 text-2xl font-heading font-bold text-[#F0EFF4]">Use the RoofClaw Local Service Ad Agent repo.</h3>
      <p className="mb-6 max-w-2xl text-[#F0EFF4]/65">The repo is MIT licensed and includes the Google LSA ingestion plan, HubSpot roofing CRM standard, Twilio-first phone setup, call guardrails, and the new Twilio onboarding fast path inspired by Patter.</p>
      <a href={REPO_URL} target="_blank" rel="noopener noreferrer" className="inline-flex rounded-full bg-[#7B61FF] px-6 py-3 font-bold text-[#F0EFF4] transition-colors hover:bg-[#8D78FF]">View the GitHub repo →</a>
    </div>
  </div>
);

const QuickAnswer = () => (
  <div className="not-prose article-summary my-10 rounded-[2rem] border border-[#10B981]/30 bg-[#0d1a14] p-7">
    <p className="mb-4 text-xs font-data uppercase tracking-[0.25em] text-[#10B981]">Quick answer</p>
    <p className="mb-5 text-lg leading-relaxed text-[#F0EFF4]/80">
      Yes: you can use AI to call Google Local Services Ads leads automatically. The safe pattern is API-first, CRM-first, and human-reviewed before live scale.
    </p>
    <ul className="grid gap-3 md:grid-cols-2">
      {[
        'Poll Google LSA leads through the Google Ads API.',
        'Write the lead into HubSpot or your CRM before calling.',
        'Use Twilio for the phone number and webhook layer.',
        'Use OpenClaw Voice Call for the AI call workflow.',
        'Keep the phone prompt narrow and escalation-heavy.',
        'Review the first 10 live calls before expanding.',
      ].map(item => (
        <li key={item} className="flex gap-3 text-sm leading-relaxed text-[#F0EFF4]/70">
          <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-[#10B981]" />
          {item}
        </li>
      ))}
    </ul>
  </div>
);

const MiniCards = ({ eyebrow, items }) => (
  <div className="not-prose my-8 rounded-2xl border border-[#F0EFF4]/10 bg-[#18181B] p-6">
    <p className="mb-5 text-xs font-data uppercase tracking-[0.25em] text-[#7B61FF]">{eyebrow}</p>
    <div className="grid gap-4 md:grid-cols-2">
      {items.map(({ title, body }) => (
        <div key={title} className="rounded-xl border border-[#F0EFF4]/10 bg-[#0A0A14] p-4">
          <h3 className="mb-2 text-base font-heading font-bold text-[#F0EFF4]">{title}</h3>
          <p className="text-sm leading-relaxed text-[#F0EFF4]/60">{body}</p>
        </div>
      ))}
    </div>
  </div>
);

const StepList = () => (
  <div className="not-prose my-10 rounded-2xl border border-[#F0EFF4]/10 bg-[#18181B] p-7">
    <p className="mb-6 text-xs font-data uppercase tracking-[0.25em] text-[#10B981]">Implementation checklist</p>
    <ol className="space-y-3">
      {HOW_TO_STEPS.map((step, index) => (
        <li key={step} className="flex gap-4 text-sm leading-relaxed text-[#F0EFF4]/70">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[#7B61FF]/40 bg-[#7B61FF]/15 font-data text-xs text-[#7B61FF]">{index + 1}</span>
          <span>{step}</span>
        </li>
      ))}
    </ol>
  </div>
);

export default function AiGoogleLsaCallingOpenClaw() {
  return (
    <BlogLayout
      title="How to Use AI to Automate Calling Google Local Services Leads with OpenClaw"
      date="2026-05-04"
      readTime="13"
      heroImage="/images/blog/ai-google-lsa-calling-openclaw-hero.jpg"
      description="A practical DIY guide for using OpenClaw, Google Local Services Ads, HubSpot, Twilio, and voice AI to call new roofing leads faster and safely."
      canonicalPath={SLUG}
      keywords="Google Local Services Ads AI calling, OpenClaw voice call, automate LSA leads, Twilio AI agent, roofing AI agent, HubSpot Google LSA, RoofClaw Local Service Ad Agent"
    >
      <StructuredData />

      <p className="text-xl text-[#F0EFF4]/80 leading-relaxed">Google Local Services Ads leads are expensive because they are high-intent.</p>

      <p>A homeowner who fills out a request is not casually browsing. They need help. If your business waits too long to respond, the lead cools off fast.</p>

      <p>That makes Google LSA a practical use case for AI automation.</p>

      <p>Not because AI should replace your sales team. Because AI can do the first, time-sensitive part while the humans are driving, selling, estimating, or standing on a roof.</p>

      <p>In this guide, we will walk through how to use OpenClaw to automate the first phone call to Google Local Services Ads leads.</p>

      <QuickAnswer />

      <Callout eyebrow="The basic workflow" variant="green">
        New Google LSA lead → OpenClaw detects it → CRM record is created or updated → Twilio places or receives the call → the AI voice agent qualifies the homeowner → the call is logged → anything risky escalates to a human.
      </Callout>

      <h2>What you are building</h2>
      <p>You are building a local AI sales assistant that can respond to Google Local Services Ads leads by phone.</p>

      <CodeBlock>{`Google Local Services Ads
  -> Google Ads API polling
  -> OpenClaw agent
  -> CRM record creation/update
  -> Twilio phone call
  -> Voice AI stack: speech-to-text, LLM, text-to-speech
  -> Call summary and next step logged in CRM
  -> Human escalation when needed`}</CodeBlock>

      <MiniCards
        eyebrow="Reference stack"
        items={[
          { title: 'OpenClaw', body: 'Runs the agent locally, with tools, memory, schedules, and voice-call capability.' },
          { title: 'Google Ads API', body: 'Retrieves new Local Services Ads leads from the source of truth.' },
          { title: 'HubSpot', body: 'Stores the contact, property, deal, call notes, and next step.' },
          { title: 'Twilio + voice AI', body: 'Twilio carries the call. Grok STT, LLM, and TTS handle the voice loop.' },
        ]}
      />

      <p>You can adapt the same principles to other CRMs or voice providers, but the core pattern remains the same.</p>

      <h2>Why not just use Zapier, n8n, or Vapi?</h2>
      <p>You can. For simple demos, no-code tools are useful.</p>
      <p>But roofing lead response has constraints that make a local agent runtime attractive.</p>

      <ul>
        <li>The agent needs memory across calls, texts, CRM records, and follow-ups.</li>
        <li>Phone calls create compliance and brand risk.</li>
        <li>CRM hygiene matters more than the demo.</li>
        <li>The system should keep working without a human babysitting a browser tab.</li>
      </ul>

      <p>OpenClaw gives the agent local files, tools, memory, schedules, messaging integrations, voice-call capability, and the ability to run on customer-owned hardware.</p>

      <h2>Step 1: Decide what the AI is allowed to do</h2>
      <p>Before APIs and webhooks, define the job.</p>
      <MiniCards
        eyebrow="The first version should be boring on purpose"
        items={[
          { title: 'Allowed', body: 'Greet the homeowner, disclose AI status when required, confirm name, phone, address, service need, urgency, and next step.' },
          { title: 'Not allowed', body: 'Quote prices, promise exact arrival windows, make insurance/warranty commitments, argue, or keep going when transcription is bad.' },
        ]}
      />

      <Callout eyebrow="Phone agents need narrow prompts" variant="amber">
        Voice agents feel magical when they work, but they are high-risk because they speak in real time. You want short turns, narrow instructions, and clear escalation triggers.
      </Callout>

      <h2>Step 2: Connect Google Local Services Ads leads</h2>
      <p>Google Local Services Ads leads are available through the Google Ads API. The practical implementation pattern is polling.</p>

      <CodeBlock>{`Every 2 minutes:
  search for recent Local Services leads
  compare against local seen-state
  process only new or changed leads
  write/update CRM record
  decide whether a response is allowed`}</CodeBlock>

      <p>A starter GAQL query can retrieve recent Local Services leads by resource name, lead ID, creation time, lead type, lead status, category, service, and contact details.</p>

      <p>For production, use a rolling lookback window and de-duplicate lead IDs locally.</p>
      <p>Do not rely only on email notifications. Email is useful as a wake signal, but the API should be treated as the source of truth.</p>

      <h2>Step 3: Create or update the CRM record first</h2>
      <p>Before calling the lead, create the CRM record. If the call happens first and the CRM update fails later, your team loses context.</p>
      <MiniCards
        eyebrow="CRM model"
        items={[
          { title: 'Contact', body: 'The person: homeowner, buyer, property manager, tenant, or decision maker.' },
          { title: 'Property', body: 'The physical address where the roofing issue exists.' },
          { title: 'Deal', body: 'The revenue opportunity tied to that person and property.' },
          { title: 'Activities', body: 'Calls, notes, messages, tasks, meetings, summaries, and next steps.' },
        ]}
      />

      <p>For HubSpot, upsert the contact, create or update the property record if your portal uses one, create the deal, associate everything, and log the source as Google Local Services Ads.</p>

      <h2>Step 4: Use Twilio for the phone layer</h2>
      <p>Twilio is the simplest default phone provider for this workflow. The AI agent still needs a public phone number, inbound/outbound call handling, webhooks, and media streaming. Twilio handles that carrier layer.</p>

      <CodeBlock>{`TWILIO_ACCOUNT_SID
TWILIO_AUTH_TOKEN
TWILIO_FROM_NUMBER
VOICE_CALL_PUBLIC_URL`}</CodeBlock>

      <p>The public URL must be reachable by Twilio. It cannot be localhost, a private LAN IP, or a plain Tailscale IP unless you are intentionally exposing it through a public mechanism such as Tailscale Funnel.</p>

      <h2>Step 5: Auto-configure the Twilio webhook</h2>
      <p>One annoying setup step is configuring the Twilio phone number’s voice webhook.</p>

      <p>Normally a builder has to log into Twilio, find the number, paste the OpenClaw webhook URL, select POST, save it, and then separately build the OpenClaw config.</p>

      <p>The RoofClaw Local Service Ad Agent repo now includes a Twilio onboarding fast path inspired by Nicolò Tognoni’s Patter project.</p>

      <p>Patter showed a clean carrier setup pattern: bind the phone carrier to the agent webhook instead of making the user manually wire everything together.</p>

      <CodeBlock>{`export TWILIO_ACCOUNT_SID=ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx
export TWILIO_AUTH_TOKEN=***
export TWILIO_FROM_NUMBER=+15555550123
export VOICE_CALL_PUBLIC_URL=https://voice.example.com/voice/webhook

npm run phone:twilio:check`}</CodeBlock>

      <MiniCards
        eyebrow="What the preflight checks"
        items={[
          { title: 'Inputs', body: 'Required env vars, E.164 phone number format, and public HTTPS webhook URL.' },
          { title: 'Twilio ownership', body: 'Confirms the phone number belongs to the Twilio account before applying changes.' },
          { title: 'Safety', body: 'Does not print the auth token and rejects private or loopback webhook hosts.' },
          { title: 'Output', body: 'Prints an OpenClaw voice-call config snippet the builder can review.' },
        ]}
      />

      <CodeBlock>{`VOICE_CALL_LIVE_MODE=true npm run phone:twilio:apply`}</CodeBlock>

      <p>That updates the Twilio number’s <code>VoiceUrl</code> to the OpenClaw webhook. It does not buy numbers, enable broad live calling, or bypass call guardrails.</p>

      <p>Credit to Nicolò and Patter for the inspiration: <a href="https://github.com/PatterAI/Patter" target="_blank" rel="noopener noreferrer">github.com/PatterAI/Patter</a>.</p>

      <h2>Step 6: Configure OpenClaw Voice Call</h2>
      <p>OpenClaw’s voice-call plugin handles the agent side of calls.</p>

      <p>A minimal configuration sets the provider to Twilio, defines the from number, adds the credentials, sets the public webhook URL, enables the voice stack, and provides a narrow phone-specific prompt.</p>
      <p>After configuration, run setup checks before a live call.</p>

      <CodeBlock>{`openclaw voicecall setup
openclaw voicecall smoke
openclaw voicecall smoke --to "+15555550123"`}</CodeBlock>

      <h2>Step 7: Choose the voice AI architecture</h2>
      <p>There are two broad ways to build AI phone calls.</p>

      <MiniCards
        eyebrow="Voice architecture choices"
        items={[
          { title: 'Realtime speech-to-speech', body: 'The model handles voice input and voice output directly. This can feel more natural when the provider support is mature.' },
          { title: 'Cascaded voice stack', body: 'Speech-to-text, LLM, and text-to-speech are separate. It is easier to inspect, debug, and swap pieces.' },
        ]}
      />
      <p>For the RoofClaw reference implementation, the recommended default is the cascaded xAI/Grok stack: Grok STT → Grok LLM → Grok TTS.</p>
      <p>That pattern is easier to inspect and debug, lets you swap components independently, and fits the way many business agents already reason with tool calls and CRM context.</p>

      <h2>Step 8: Write a phone-specific prompt</h2>
      <p>Do not reuse the agent’s full personality prompt as the phone prompt.</p>

      <p>A phone call needs a narrower context: identity and disclosure rules, approved call script, qualification questions, escalation triggers, CRM logging requirements, and what the agent may not promise.</p>

      <CodeBlock>{`You are the phone voice of a roofing sales assistant.
Speak in short turns.
Ask one question at a time.
Confirm name, phone, address, and service need.
Never quote pricing.
Never promise exact scheduling unless calendar rules are connected.
Escalate angry, legal, insurance-heavy, emergency, or unclear calls.
Log the call summary and next step in the CRM.`}</CodeBlock>

      <h2>Step 9: Start with one test number</h2>
      <p>Do not point the AI at the public office number first. Use a dedicated Twilio test number that is not advertised publicly.</p>
      <p>Test outbound calls, inbound calls, no-answer behavior, voicemail behavior, hangups, escalation, CRM logging, transcript quality, and whether the agent stops when confused.</p>
      <p>If the Google LSA number is also the main office number, you may need CallRail or another routing layer in front of Twilio.</p>

      <p>Do not casually rewrite webhooks on the public business number unless you understand the routing impact.</p>

      <h2>Step 10: Human-review the first live calls</h2>
      <p>The first live phase should be narrow:</p>

      <ul>
        <li>business hours only;</li>
        <li>new Google LSA leads only;</li>
        <li>one first-response call attempt;</li>
        <li>no pricing promises;</li>
        <li>no complex scheduling unless approved;</li>
        <li>first 10 real calls reviewed by a human.</li>
      </ul>
      <p>For each call, log timestamp, direction, phone number, associated records, outcome, short summary, next step, and escalation if needed.</p>

      <StepList />

      <h2>Common mistakes</h2>
      <h3>Calling before CRM logging works</h3>
      <p>If the CRM record fails, your AI call becomes disconnected from the sales process. Create or update the CRM first.</p>
      <h3>Using a temporary tunnel in production</h3>
      <p>Temporary tunnel URLs are fine for tests. They are not a production plan. Use a stable public HTTPS URL.</p>
      <h3>Giving the phone agent too much memory</h3>
      <p>The phone agent does not need access to every internal file, private owner conversation, or unrelated tool. Narrow the prompt and tools.</p>
      <h3>Letting the AI book appointments without rules</h3>
      <p>Booking is not just “put something on the calendar.” You need territory, availability, crew capacity, service type, urgency, and confirmation rules.</p>
      <h3>Skipping disclosure and recording rules</h3>
      <p>Call recording, AI disclosure, consent, TCPA, CASL, and local rules matter. Do not guess. Define the company policy before live calling.</p>

      <h2>The open-source RoofClaw repo</h2>
      <p>If you want a starting point, use the RoofClaw Local Service Ad Agent repo.</p>

      <p>It includes Google LSA ingestion guidance, HubSpot roofing CRM standards, Twilio-first phone setup, xAI/Grok voice stack recommendations, call guardrails, and dry-run-first stages.</p>

      <p>It also includes the Twilio onboarding fast path inspired by Patter.</p>

      <RepoCta />

      <h2>Why use RoofClaw instead of building a one-off agent?</h2>
      <p>If you are technical, you can build this yourself. OpenClaw is open. Twilio has APIs. Google Ads has APIs. HubSpot has APIs. The pieces are there.</p>
      <p>But here is the strategic question: why spend all that effort to build an agent that only does one thing?</p>
      <p>A properly configured RoofClaw can handle the Google LSA phone workflow and support the rest of the revenue desk too:</p>

      <ul>
        <li>CRM hygiene;</li>
        <li>follow-up reminders;</li>
        <li>sales notes;</li>
        <li>inbox triage;</li>
        <li>customer support workflows;</li>
        <li>internal reporting;</li>
        <li>appointment prep;</li>
        <li>local business memory.</li>
      </ul>
      <p>The Google LSA call agent is not the whole value. It is one skill inside a dedicated AI employee that runs on customer-owned hardware, with local memory, secure credentials, and a role designed around the business.</p>
      <p>So if you want to learn, build it yourself. If you want the outcome without turning into an AI telephony engineer, use RoofClaw.</p>
      <p>The repo is the map. RoofClaw is the vehicle.</p>

      <h2>FAQ</h2>
      {FAQ_ITEMS.map(({ q, a }) => (
        <div key={q}>
          <h3>{q}</h3>
          <p>{a}</p>
        </div>
      ))}

      <h2>Final checklist</h2>
      <ul>
        <li>Sales agent role is clearly defined.</li>
        <li>Google LSA API access works in read-only mode.</li>
        <li>CRM contact, deal, and property logging works.</li>
        <li>Twilio number is dedicated or routing is approved.</li>
        <li>Public webhook URL is stable and HTTPS.</li>
        <li>OpenClaw Voice Call setup passes.</li>
        <li>Phone prompt is narrow and approved.</li>
        <li>Call recording and AI disclosure rules are defined.</li>
        <li>Human escalation path works.</li>
        <li>First 10 live calls will be reviewed.</li>
      </ul>

      <p>Do those things, and you have more than a chatbot. You have the first version of an AI revenue desk.</p>
    </BlogLayout>
  );
}
